/**
 * OperatorGuard — enforces Op 1/2/3 separation.
 *
 * If the user's operatorMode doesn't match the required operator for this route,
 * they are redirected to their own operator home instead of seeing another operator's tools.
 *
 * Admins bypass this guard and can access all operator frameworks.
 */
import { useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/_core/hooks/useAuth";

interface OperatorGuardProps {
  children: React.ReactNode;
  /** The operator mode required to access this route: "op1" | "op2" | "op3" */
  requiredMode: "op1" | "op2" | "op3";
}

const OP_HOME: Record<string, string> = {
  op1: "/command/op1",
  op2: "/command/op2",
  op3: "/command/op3",
};

export default function OperatorGuard({ children, requiredMode }: OperatorGuardProps) {
  const { user, loading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (loading) return;
    if (!user) return; // VIPGuard handles unauthenticated

    // Admins can access all operator frameworks
    if (user.role === "admin") return;

    const userMode = (user as any).operatorMode as string | null;

    // If user has no operator mode set, send them to onboarding
    if (!userMode) {
      setLocation("/onboarding");
      return;
    }

    // If user's mode doesn't match this route's required mode, redirect to their home
    if (userMode !== requiredMode) {
      const home = OP_HOME[userMode] ?? "/command/dashboard";
      setLocation(home);
    }
  }, [loading, user, requiredMode, setLocation]);

  if (loading) return null;
  if (!user) return null;

  const userMode = (user as any).operatorMode as string | null;

  // Admins bypass
  if (user.role === "admin") return <>{children}</>;

  // No mode → onboarding handles it
  if (!userMode) return null;

  // Wrong mode → redirect handles it
  if (userMode !== requiredMode) return null;

  return <>{children}</>;
}
